import { useEffect, useState } from 'react';

import { useIsMobile } from '~/domains/common/hooks/mediaQueries';

import {
  SUB_HEADER_HEIGHT,
  SUB_HEADER_HEIGHT_NO_NAV,
  SUB_HEADER_HEIGHT_WITH_WIDGET,
  SUB_HEADER_STICKY_POSITION,
  SUB_HEADER_SUBNAV_HEIGHT,
} from '../../constants';

import useStyles from './SubHeader.styles';
import type { SubHeaderWrapperProps } from './types';

function useSubHeader({
  widget,
  isSticky,
  subMenuItems,
  subNavVariant,
}: Omit<SubHeaderWrapperProps, 'title'>) {
  const hasWidget = widget?.items?.length > 0 || widget?.actions?.length > 0;
  const hasSubMenuItems = subMenuItems && subMenuItems.length > 0;
  const { classes } = useStyles({
    isSticky,
    hasWidget,
    hasSubMenuItems,
    subNavVariant,
  });
  const isMobile = useIsMobile();

  // eslint-disable-next-line no-nested-ternary
  const HEIGHT = !hasSubMenuItems
    ? SUB_HEADER_HEIGHT_NO_NAV
    : hasWidget && isMobile
      ? SUB_HEADER_HEIGHT_WITH_WIDGET
      : SUB_HEADER_HEIGHT;

  const [subHeaderHeight, setSubHeaderHeight] = useState(HEIGHT);
  const [subHeaderWidgetOpacity, setSubHeaderWidgetOpacity] = useState(1);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;

      // Calculate scroll progress after header becomes sticky
      const scrollProgress = Math.max(SUB_HEADER_STICKY_POSITION, scrollTop);
      const minHeight = SUB_HEADER_SUBNAV_HEIGHT;

      // Distance to complete animation. Non-sticky header dissapears completely
      const maxScrollDistance = isSticky ? HEIGHT - minHeight : HEIGHT;

      // Calculate animation progress (0 to 1)
      const progress = Math.min(scrollProgress / maxScrollDistance, 1);

      // Interpolate height from SUB_HEADER_HEIGHT to minHeight
      const newHeight = HEIGHT - progress * maxScrollDistance;
      setSubHeaderHeight(newHeight);
      setSubHeaderWidgetOpacity(1 - progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [HEIGHT, isSticky]);

  return {
    classes,
    isMobile,
    subHeaderContainerHeight: HEIGHT,
    subHeaderHeight,
    subHeaderWidgetOpacity,
  };
}

export default useSubHeader;
